import { useMemo } from 'react';
import Box from '@mui/material/Box';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import { useTranslation } from 'react-i18next';

import { ModalV2, ButtonV2, dsV2 } from '@bloomays-lib/ui.shared';
import type { IMissionCardDetails, IMissionCardPricing } from '@bloomays-lib/types.shared';
import { dateFormat, getFirstValidPrice } from '@bloomays-lib/utils.shared';

import { InfoV2 } from '../formFields';

export type PriceHistoryModalProps = {
  open: boolean;
  onClose: () => void;
  onCreateAmendment: () => void;
  mission: IMissionCardDetails;
};

const fmtEuro = (value?: number | null): string =>
  value == null ? '—' : `${Math.round(value).toLocaleString('fr-FR')} €`;
const fmtDate = (value?: Date | string | null): string => (value ? dateFormat(value, 'dd/MM/yyyy') : '—');

const thSx = {
  textAlign: 'left' as const,
  fontSize: 11,
  fontWeight: 600,
  color: dsV2.color.blanc,
  background: dsV2.color.bleu,
  padding: '13px 16px',
  whiteSpace: 'nowrap' as const,
};
const tdSx = {
  padding: '13px 16px',
  borderBottom: `1px solid ${dsV2.color.borderLight}`,
  color: dsV2.color.t2,
  whiteSpace: 'nowrap' as const,
  fontSize: 13,
};
const numSx = { textAlign: 'right' as const, fontFamily: dsV2.font.sans, fontVariantNumeric: 'tabular-nums' as const };

/** Historique des prix (ModalV2) — lecture seule (piloté par les avenants). Refonte V2 de `PricingHistory`. */
export const PriceHistoryModal = ({ open, onClose, onCreateAmendment, mission }: PriceHistoryModalProps) => {
  const { t } = useTranslation(['missionDashboard']);
  const rows = useMemo<IMissionCardPricing[]>(() => {
    const today = new Date();
    const currentPrice = mission.prices ? getFirstValidPrice(mission.prices, today) : undefined;
    return [...(mission.prices ?? [])]
      .sort((a, b) => new Date(b.startDate ?? 0).getTime() - new Date(a.startDate ?? 0).getTime())
      .map((p) => ({
        ...p,
        isCurrent: p === currentPrice,
      }));
  }, [mission.prices]);

  return (
    <ModalV2
      open={open}
      onClose={onClose}
      icon={<VisibilityRoundedIcon sx={{ fontSize: 18 }} />}
      iconTone="bleu"
      title={t('actions.priceHistory', { ns: 'missionDashboard' })}
      width={720}
      footer={
        <>
          <ButtonV2 variant="secondary" onClick={onClose}>
            {t('drawers.common.close', { ns: 'missionDashboard' })}
          </ButtonV2>
          <ButtonV2 onClick={onCreateAmendment} startIcon={<AutoAwesomeRoundedIcon sx={{ fontSize: 15 }} />}>
            {t('actions.createAmendment', { ns: 'missionDashboard' })}
          </ButtonV2>
        </>
      }
    >
      <InfoV2 tone="blue" icon={<LockRoundedIcon sx={{ fontSize: 16 }} />}>
        {t('actionModals.priceHistory.readOnlyNoteBefore', { ns: 'missionDashboard' })}
        <strong>{t('actionModals.priceHistory.readOnlyNoteStrong', { ns: 'missionDashboard' })}</strong>.
      </InfoV2>
      <Box sx={{ border: `1px solid ${dsV2.color.border}`, borderRadius: dsV2.radius.sm, overflow: 'auto' }}>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 560 }}>
          <Box component="thead">
            <Box component="tr">
              <Box component="th" sx={thSx}>
                {t('actionModals.priceHistory.colValidFrom', { ns: 'missionDashboard' })}
              </Box>
              <Box component="th" sx={{ ...thSx, ...numSx }}>
                {t('actionModals.priceHistory.colPurchase', { ns: 'missionDashboard' })}
              </Box>
              <Box component="th" sx={{ ...thSx, ...numSx }}>
                {t('actionModals.priceHistory.colSale', { ns: 'missionDashboard' })}
              </Box>
              <Box component="th" sx={{ ...thSx, ...numSx }}>
                {t('actionModals.priceHistory.colTva', { ns: 'missionDashboard' })}
              </Box>
              <Box component="th" sx={{ ...thSx, ...numSx }}>
                {t('actionModals.priceHistory.colStandby', { ns: 'missionDashboard' })}
              </Box>
            </Box>
          </Box>
          <Box component="tbody">
            {rows.map((p, i) => {
              const currentRow = p.isCurrent === true;
              const rowSx = currentRow
                ? { '& td': { background: dsV2.color.bleuDim, color: dsV2.color.noir, fontWeight: 600 } }
                : undefined;
              return (
                <Box component="tr" key={p.recordId ?? i} sx={rowSx}>
                  <Box component="td" sx={tdSx}>
                    {fmtDate(p.startDate)}
                    {currentRow ? (
                      <Box
                        component="span"
                        sx={{
                          marginLeft: '8px',
                          fontSize: '9.5px',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          color: dsV2.color.bleu,
                          background: dsV2.color.blanc,
                          border: '1px solid rgba(21,97,235,0.3)',
                          borderRadius: '100px',
                          padding: '2px 8px',
                        }}
                      >
                        {t('actionModals.priceHistory.inForce', { ns: 'missionDashboard' })}
                      </Box>
                    ) : null}
                  </Box>
                  <Box component="td" sx={{ ...tdSx, ...numSx }}>
                    {fmtEuro(p.purchasePricePerDayWithoutTaxes)}
                  </Box>
                  <Box component="td" sx={{ ...tdSx, ...numSx }}>
                    {fmtEuro(p.salePricePerDayWithoutTaxes)}
                  </Box>
                  <Box component="td" sx={{ ...tdSx, ...numSx }}>
                    {p.taxes != null ? `${p.taxes} %` : '—'}
                  </Box>
                  <Box component="td" sx={{ ...tdSx, ...numSx }}>
                    {p.astreinteCoef != null ? `×${p.astreinteCoef}` : '—'}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </ModalV2>
  );
};

export default PriceHistoryModal;
